import db from './db';
import type { KnittingState, Pattern } from '../types/state';
import { CURRENT_PATTERN_VERSION, CURRENT_STATE_VERSION } from '../types/state';
import type { Session } from '../types/session';
import type { IRPattern } from '../types/ir';
import type { z } from 'zod';
import { PatternSchema, SessionWireSchema } from '../types/schemas';
import { encodeSession, decodeSession } from './eventCodec';
import type { SessionWire } from './eventCodec';
import { archiveHistory } from '../logic/archive';
import { sessionActivityDays } from '../logic/activity';
import { normalizePattern, normalizeSession, patternTitlesFromRaw } from './migrations';
import { bumpLastSavedAt } from './lastSaved';
import { markSaveOk, markSaveError } from './saveStatus';
import { track } from '../analytics/posthog';
import { captureException } from '../analytics';
import {
  writeSessionMirror,
  readSessionMirror,
  clearSessionMirror,
  clearSessionMirrorIfUnchanged,
} from './sessionMirror';
import { markRecovered } from './recoveryNotice';
import { postStateChanged, subscribeRemoteStateChanged } from './stateChannel';

type WirePattern = z.infer<typeof PatternSchema>;

const STATE_KEY = 'knitting-state';
const BACKUP_KEY = 'knitting-state-backup';
// The wire blob most recently written (or loaded) — rotated to BACKUP_KEY on
// the next successful save, giving one previous-good generation to fall back
// to when the main blob goes missing or unreadable.
let lastWrittenWire: unknown = null;

type ChangeCallback = () => void;
const changeCallbacks: ChangeCallback[] = [];

export function onStateChanged(cb: ChangeCallback): () => void {
  changeCallbacks.push(cb);
  return () => {
    const idx = changeCallbacks.indexOf(cb);
    if (idx >= 0) changeCallbacks.splice(idx, 1);
  };
}

function notifyChange(): void {
  changeCallbacks.forEach((cb) => cb());
}

/**
 * Convert an in-memory Pattern into its on-disk wire form: serialize
 * `structure` to `structureJson` (preserves node IDs across reloads), drop
 * `structure`, and stamp the per-item version.
 */
export function toWirePattern(p: Pattern): WirePattern {
  const wire: WirePattern = {
    version: CURRENT_PATTERN_VERSION,
    id: p.id,
    title: p.title,
    parentPatternId: p.parentPatternId,
    dsl: p.dsl,
  };
  if (p.structure) wire.structureJson = JSON.stringify(p.structure);
  else if (p.structureJson) wire.structureJson = p.structureJson;
  if (p.sourceUrl) wire.sourceUrl = p.sourceUrl;
  if (p.upstreamHash) wire.upstreamHash = p.upstreamHash;
  if (p.builtin !== undefined) wire.builtin = p.builtin;
  if (p.js_source !== undefined) wire.js_source = p.js_source;
  if (p.color !== undefined) wire.color = p.color;
  if (p.pdfRef !== undefined) wire.pdfRef = p.pdfRef;
  return wire;
}

interface ParsedStateBlob {
  version: number;
  patterns: Record<string, Pattern>;
  sessions: Record<string, Session>;
  unknownPatterns: Record<string, unknown>;
  unknownSessions: Record<string, unknown>;
}

/**
 * Parse a persisted state envelope per-item. Each pattern and session is
 * validated independently; failures are preserved as opaque blobs in
 * `unknownPatterns` / `unknownSessions` and round-tripped through saves so a
 * future client that understands the shape can pick them up.
 *
 * The envelope itself is intentionally permissive — any shape that isn't a
 * `{ patterns, sessions }` record is treated as empty. There's no way for
 * envelope-level validation to take down item-level data.
 */
export async function parseStateBlob(raw: unknown): Promise<ParsedStateBlob> {
  const obj = isObject(raw) ? raw : {};
  const rawPatterns = isObject(obj.patterns) ? obj.patterns : {};
  const rawSessions = isObject(obj.sessions) ? obj.sessions : {};
  const version = typeof obj.version === 'number' ? obj.version : CURRENT_STATE_VERSION;

  const patterns: Record<string, Pattern> = {};
  const unknownPatterns: Record<string, unknown> = {};
  for (const [id, p] of Object.entries(rawPatterns)) {
    const result = PatternSchema.safeParse(normalizePattern(p));
    if (!result.success) {
      console.warn(
        `Pattern ${id} failed schema validation; preserving raw blob.`,
        result.error.issues,
      );
      unknownPatterns[id] = p;
      track('unknown_item_preserved', { kind: 'pattern', count: 1 });
      continue;
    }
    let structure = tryParseStructureJson(result.data.structureJson);
    if (!structure) structure = await tryParseDSL(result.data.dsl, result.data.title);
    if (!structure) {
      // Schema-valid but neither the cached IR nor the DSL parses on this
      // client — typically an old client reading newer DSL grammar, or
      // structureJson written against a future IR shape. Quarantine instead
      // of letting the pattern through with no structure.
      console.warn(`Pattern ${id} has unparseable DSL/IR on this client; preserving raw blob.`);
      unknownPatterns[id] = p;
      track('unknown_item_preserved', { kind: 'pattern', count: 1 });
      continue;
    }
    patterns[id] = toDomainPattern(result.data, structure);
  }

  const titles = patternTitlesFromRaw(rawPatterns);
  const sessions: Record<string, Session> = {};
  const unknownSessions: Record<string, unknown> = {};
  for (const [id, s] of Object.entries(rawSessions)) {
    const result = SessionWireSchema.safeParse(normalizeSession(s, titles));
    if (!result.success) {
      console.warn(`Session ${id} failed validation; preserving raw blob.`, result.error.issues);
      unknownSessions[id] = s;
      track('unknown_item_preserved', { kind: 'session', count: 1 });
      continue;
    }
    sessions[id] = decodeSession(result.data);
  }

  return { version, patterns, sessions, unknownPatterns, unknownSessions };
}

function isObject(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

// Strip the wire-only `version` so domain code can't see it.
function toDomainPattern(wire: WirePattern, structure: IRPattern | undefined): Pattern {
  const p: Pattern = {
    id: wire.id,
    title: wire.title,
    parentPatternId: wire.parentPatternId,
    dsl: wire.dsl,
  };
  if (wire.structureJson !== undefined) p.structureJson = wire.structureJson;
  if (structure) p.structure = structure;
  if (wire.sourceUrl) p.sourceUrl = wire.sourceUrl;
  if (wire.upstreamHash) p.upstreamHash = wire.upstreamHash;
  if (wire.builtin !== undefined) p.builtin = wire.builtin;
  if (wire.js_source !== undefined) p.js_source = wire.js_source;
  // Only accept a 6-char hex color into the domain. Anything else (empty
  // string, a future `#rrggbbaa`, named CSS color, etc.) is silently dropped;
  // resolvePatternColor falls back to deriveColor(id) at render time.
  if (typeof wire.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(wire.color)) {
    p.color = wire.color;
  }
  if (wire.pdfRef) p.pdfRef = wire.pdfRef;
  return p;
}

/**
 * Build the on-disk envelope from an in-memory state, merging any
 * `unknownPatterns` / `unknownSessions` back into the records so they survive
 * the save. Stamps `CURRENT_STATE_VERSION` on the envelope; per-item versions
 * are stamped by `toWirePattern` and `encodeSession`.
 */
export function toWireState(state: KnittingState): {
  version: number;
  patterns: Record<string, unknown>;
  sessions: Record<string, unknown>;
} {
  const patterns: Record<string, unknown> = {};
  for (const [id, p] of Object.entries(state.patterns)) patterns[id] = toWirePattern(p);
  for (const [id, blob] of Object.entries(state.unknownPatterns ?? {})) patterns[id] = blob;

  const sessions: Record<string, SessionWire | unknown> = {};
  for (const [id, s] of Object.entries(state.sessions)) sessions[id] = encodeSession(s);
  for (const [id, blob] of Object.entries(state.unknownSessions ?? {})) sessions[id] = blob;

  return { version: CURRENT_STATE_VERSION, patterns, sessions };
}

// Serializes every mutating operation (savePattern, updateSession, etc.) so a
// concurrent fire-and-forget save (e.g. the tracker's auto-save useEffect)
// can't read a stale state snapshot, then write it back over an interleaved
// fork. Without this, two read-modify-write pipelines can race and the older
// snapshot wins, silently dropping the more recent change.
let writeQueue: Promise<unknown> = Promise.resolve();
function serializeWrite<T>(fn: () => Promise<T>): Promise<T> {
  const next = writeQueue.then(fn, fn);
  // Swallow rejections in the queue chain so one failure doesn't poison
  // subsequent writes; the original promise still rejects to the caller.
  writeQueue = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

// ─── Canonical in-memory state + coalesced write-through ────────────────────
//
// The blob is read+validated once per app life; all reads serve a clone of
// the in-memory canonical state, and all mutations edit it directly and
// schedule an async write-through. Rapid mutations coalesce: at most one
// IDB write in flight and one pending serialization behind it, so N row taps
// cost ≤2 writes instead of N full read-validate-encode-write cycles.
let canonicalState: KnittingState | null = null;
let loadPromise: Promise<KnittingState> | null = null;
let dirty = false;
let writeInFlight = false;
let pendingWaiters: Array<{ resolve: () => void; reject: (e: unknown) => void }> = [];
// Which session the localStorage mirror currently protects (the most recently
// written one — the active tracker session in practice).
let lastMirroredSessionId: string | null = null;
// The `savedAt` of the mirror payload boot reconciliation restored, so the
// post-commit clear only drops that exact payload and never a fresher one a
// live mutation wrote while the boot commit was in flight.
let reconciledMirrorSavedAt: string | null = null;
// Set when boot reconciliation restores a session from the mirror; cleared
// the moment a live mutation supersedes that session — see
// confirmMirrorRecovery. This flag only gates whether such a write must
// bypass the content-identical no-op skip below (updateSession); the
// recovery notice's own lifetime is owned entirely by RecoveryToast
// (auto-dismiss timer + manual dismiss), not by this flag.
let unconfirmedRecoveredSessionId: string | null = null;
// Bumped every time something OTHER than the in-progress cold load becomes
// authoritative for `canonicalState` (currently: `saveFullState` and
// `__resetPersistenceForTest`). A cold load in `ensureLoaded()` checks this
// after its async `db.getItem` to detect "a whole-state replace (e.g. a sync
// pull) landed while I was still reading the old blob" and, if so, discards
// its own stale result instead of clobbering the newer authoritative state.
let stateGeneration = 0;

async function ensureLoaded(): Promise<KnittingState> {
  if (canonicalState) return canonicalState;
  if (!loadPromise) {
    const myGeneration = stateGeneration;
    loadPromise = (async () => {
      let raw = await db.getItem<unknown>(STATE_KEY);
      if (raw != null && !isObject(raw)) raw = null; // unreadable-as-envelope
      let recoveredFromBackup = false;
      if (raw == null) {
        const backup = await db.getItem<unknown>(BACKUP_KEY);
        if (isObject(backup)) {
          // Main blob lost or corrupt but a previous good generation exists.
          // Without this, the app silently presents as a fresh install.
          // (markRecovered/track/lastWrittenWire are deferred past the
          // generation check below so a discarded load has no side effects.)
          raw = backup;
          recoveredFromBackup = true;
        }
      }
      const parsed = await parseStateBlob(raw ?? {});
      if (myGeneration !== stateGeneration) {
        if (canonicalState) {
          // saveFullState() (or a test reset) became authoritative while this
          // read was in flight — that write wins; don't clobber it with the
          // stale blob we just finished reading.
          return canonicalState;
        }
        // Something invalidated the cache (e.g. a remote BroadcastChannel
        // ping) while we were still reading the OLD blob, and nothing else
        // has installed fresher state yet. We must NOT install our
        // (possibly stale) result here — the invalidation means a newer
        // write may already be sitting in IDB. Discard this load entirely
        // (no side effects below have fired) and retry fresh so the re-read
        // happens at the new generation.
        loadPromise = null;
        return ensureLoaded();
      }
      lastWrittenWire = raw;
      if (recoveredFromBackup) {
        markRecovered('backup');
        track('state_recovered_from_backup', {});
      }
      const state: KnittingState = {
        version: parsed.version,
        patterns: parsed.patterns,
        sessions: parsed.sessions,
        unknownPatterns: parsed.unknownPatterns,
        unknownSessions: parsed.unknownSessions,
      };
      const restoredFromMirror = reconcileMirrorInto(state);
      canonicalState = state;
      stateGeneration++;
      if (restoredFromMirror || recoveredFromBackup) {
        // Persist the restore, then drop the mirror it came from. Failure is
        // fine — the mirror stays and reconciliation re-runs next boot.
        const savedAt = reconciledMirrorSavedAt;
        commitCanonical()
          .then(() => {
            // Only drop the exact payload we committed. A live mutation may
            // have written a newer mirror (same session, later savedAt) while
            // this commit was in flight; that one's own write may not have
            // landed yet, so clearing it would reopen the loss window.
            if (restoredFromMirror && savedAt) clearSessionMirrorIfUnchanged(savedAt);
          })
          .catch(() => undefined);
      }
      return state;
    })().catch((e) => {
      loadPromise = null; // allow retry on next call
      throw e;
    });
  }
  return loadPromise;
}

/**
 * Boot-time mirror reconciliation. If the localStorage mirror holds a session
 * newer than (or absent from) the loaded blob, restore it — that's the "the
 * async IDB write didn't land before the process died" case. Blob same-or-
 * newer → blob wins and the mirror is dropped. Returns true if it restored.
 */
function reconcileMirrorInto(state: KnittingState): boolean {
  const mirror = readSessionMirror();
  if (!mirror) return false;
  const titles: Record<string, string> = {};
  for (const [id, p] of Object.entries(state.patterns)) titles[id] = p.title;
  const result = SessionWireSchema.safeParse(normalizeSession(mirror.session, titles));
  if (!result.success) {
    clearSessionMirror();
    return false;
  }
  const restored = decodeSession(result.data);
  const existing = state.sessions[restored.id];
  if (existing && existing.updatedAt >= restored.updatedAt) {
    clearSessionMirror();
    return false;
  }
  state.sessions[restored.id] = restored;
  // If the blob's copy of this session was quarantined (failed schema parse),
  // it still lives in unknownSessions — and toWireState writes those blobs
  // *after* sessions, so the corrupt copy would clobber the good one we just
  // restored on the very next save. Drop it: the mirror is authoritative here.
  if (state.unknownSessions) delete state.unknownSessions[restored.id];
  lastMirroredSessionId = restored.id;
  reconciledMirrorSavedAt = mirror.savedAt;
  // Not yet "confirmed": if the very next write to this session supersedes it
  // (the common case — a live tracker resumes and immediately re-saves), the
  // recovery was moot in practice and the notice is cleared rather than
  // surfacing a stale/confusing toast for data nobody actually lost.
  unconfirmedRecoveredSessionId = restored.id;
  markRecovered('mirror');
  track('session_recovered_from_mirror', {});
  return true;
}

/**
 * If `sessionId` is the session most recently restored-but-not-yet-confirmed
 * from the mirror, this write supersedes it: clear the flag so subsequent
 * writes go back through the normal content-identical no-op skip.
 *
 * Deliberately does NOT touch the recovery notice — the tracker auto-saves
 * on mount, so if this cleared the notice too, the "Recovered unsaved
 * knitting progress" toast would vanish within milliseconds of the very
 * screen it exists to reassure the user about. The notice's lifetime belongs
 * solely to RecoveryToast (its own auto-dismiss timer + manual dismiss).
 */
function confirmMirrorRecovery(sessionId: string): void {
  if (unconfirmedRecoveredSessionId === sessionId) {
    unconfirmedRecoveredSessionId = null;
  }
}

/** Mutation-side mirror write: synchronous, before the async write-through. */
function mirrorSession(session: Session): void {
  writeSessionMirror(encodeSession(session));
  lastMirroredSessionId = session.id;
}

/** Content equality ignoring the updatedAt stamp (wire-form compare). */
function sessionContentEquals(a: Session, b: Session): boolean {
  const strip = (s: Session) => ({ ...encodeSession(s), updatedAt: '' });
  return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
}

/** Marks the canonical state dirty; resolves when it is durably written. */
function commitCanonical(): Promise<void> {
  dirty = true;
  return new Promise((resolve, reject) => {
    pendingWaiters.push({ resolve, reject });
    void pumpWrites();
  });
}

async function pumpWrites(): Promise<void> {
  if (writeInFlight) return; // the running pump will pick up new dirt
  writeInFlight = true;
  try {
    while (dirty) {
      dirty = false;
      // Waiters captured now had their mutations applied before this
      // serialization; anyone arriving later re-flags dirty and waits for
      // the next iteration's write.
      const waiters = pendingWaiters;
      pendingWaiters = [];
      try {
        await persistCanonical();
        waiters.forEach((w) => w.resolve());
      } catch (e) {
        waiters.forEach((w) => w.reject(e));
        // The canonical state is still unpersisted. Re-flag it dirty so a
        // later kick (a mutation, flushNow on pagehide/hidden) retries with
        // the latest state. Waiters that queued while this write was failing
        // still deserve an attempt of their own (their promise must settle),
        // so keep looping for them — but once nobody is waiting, break
        // rather than spin the pump hot on a persistent failure (e.g. quota).
        dirty = true;
        if (pendingWaiters.length === 0) break;
      }
    }
  } finally {
    writeInFlight = false;
  }
}

// Web Locks (Safari 15.4+/Chrome 69+/Firefox 96+) order same-named critical
// sections across tabs; where unsupported we fall back to the per-tab queue
// only (today's behavior).
type LocksNavigator = Navigator & {
  locks?: { request<T>(name: string, cb: () => Promise<T> | T): Promise<T> };
};
async function withWriteLock<T>(fn: () => Promise<T>): Promise<T> {
  const nav = typeof navigator !== 'undefined' ? (navigator as LocksNavigator) : undefined;
  if (nav?.locks?.request) return nav.locks.request('knittime-state-write', fn);
  return fn();
}

// WebKit can wedge an IndexedDB connection after a background/restore cycle:
// requests neither resolve nor reject. Un-guarded, one hung write holds
// `writeInFlight` forever and silently stalls every subsequent save for the
// rest of the page's life — the mirror still protects the active session,
// but nothing else persists and no banner shows. Give up after this long,
// surface the error, and leave the state dirty so a later kick retries with
// the latest canonical state. Caveat: the abandoned write may keep holding
// the Web Lock until it settles, so retries can keep timing out — that's
// acceptable; the banner keeps warning while the mirror keeps the active
// session safe.
const DEFAULT_WRITE_TIMEOUT_MS = 10_000;
let writeTimeoutMs = DEFAULT_WRITE_TIMEOUT_MS;

class WriteTimeoutError extends Error {
  constructor(ms: number) {
    super(`state write did not settle within ${ms}ms`);
    this.name = 'WriteTimeoutError';
  }
}

function withWriteTimeout<T>(p: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new WriteTimeoutError(writeTimeoutMs)), writeTimeoutMs);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

async function persistCanonical(): Promise<void> {
  if (!canonicalState) return;
  const wire = toWireState(canonicalState);
  try {
    await withWriteTimeout(withWriteLock(() => db.setItem(STATE_KEY, wire)));
  } catch (e) {
    const isTimeout = e instanceof WriteTimeoutError;
    const isQuota =
      e instanceof Error &&
      (e.name === 'QuotaExceededError' || e.message.toLowerCase().includes('quota'));
    const kind = isTimeout ? ('timeout' as const) : isQuota ? ('quota' as const) : ('unknown' as const);
    track('save_failed', { kind });
    if (!isQuota) {
      captureException(e, { source: 'saveFullState' });
    }
    markSaveError(kind);
    throw e;
  }
  markSaveOk();
  // Rotate the previous good generation. Best-effort: a backup failure must
  // not fail the (already durable) primary save.
  const prev = lastWrittenWire;
  lastWrittenWire = wire;
  if (prev != null) {
    await db.setItem(BACKUP_KEY, prev).catch(() => undefined);
  }
  // Secondary bookkeeping must never fail the already-durable save.
  await bumpLastSavedAt().catch(() => undefined);
  notifyChange();
  postStateChanged();
}

/**
 * Synchronous best-effort flush for lifecycle events (pagehide / hidden):
 * rewrite the mirror from canonical state so it reflects the very latest
 * mutation, and kick the async pump in case a write can still complete.
 */
export function flushNow(): void {
  if (canonicalState && lastMirroredSessionId) {
    const s = canonicalState.sessions[lastMirroredSessionId];
    if (s) writeSessionMirror(encodeSession(s));
  }
  if (dirty) void pumpWrites();
}

// Test-only: drop all module state so the next read hits IndexedDB again.
export function __resetPersistenceForTest(): void {
  canonicalState = null;
  loadPromise = null;
  stateGeneration++;
  dirty = false;
  writeInFlight = false;
  pendingWaiters = [];
  lastMirroredSessionId = null;
  reconciledMirrorSavedAt = null;
  unconfirmedRecoveredSessionId = null;
  lastWrittenWire = null;
  writeTimeoutMs = DEFAULT_WRITE_TIMEOUT_MS;
}

// Test-only: shrink the hung-write watchdog so tests don't wait 10s.
export function __setWriteTimeoutForTest(ms: number): void {
  writeTimeoutMs = ms;
}

/**
 * Retrieves the full application state from local storage.
 *
 * Sessions are stored on disk in the compact wire form; this decodes them
 * back into the verbose in-memory `KnittingEvent[]` representation that the
 * engine and state machine consume. Per-item validation failures are
 * captured into `unknownPatterns` / `unknownSessions` so a single bad item
 * can't take down the rest of the library.
 */
export async function getFullState(): Promise<KnittingState> {
  try {
    const state = await ensureLoaded();
    // Clone: callers treat the result as their own (App seeds patterns onto
    // it, tests mutate it); the canonical copy must only change through the
    // mutator APIs below.
    return structuredClone(state);
  } catch (e) {
    captureException(e, { source: 'persistence', operation: 'loadState' });
    throw e;
  }
}

// Restore a pattern structure from a serialized JSON string. Returns undefined
// on any failure so the caller can fall back to DSL re-parsing.
function tryParseStructureJson(json: string | undefined) {
  if (!json) return undefined;
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

// Parse a stored pattern's DSL, returning undefined (and warning) on failure
// rather than throwing. This keeps a single corrupt pattern from taking down
// the whole library load.
//
// The parser (Jison-generated grammar, ~8 KB gz) is dynamic-imported on first
// call so it stays out of the eager bundle. For patterns that store
// `structureJson` alongside the DSL (the common case), the fallback never
// fires and the parser chunk is never loaded.
let parseDSLPromise: Promise<typeof import('../logic/parser')['parseDSL']> | null = null;
function getParseDSL() {
  if (!parseDSLPromise) {
    parseDSLPromise = import('../logic/parser').then((m) => m.parseDSL);
  }
  return parseDSLPromise;
}

async function tryParseDSL(dsl: string, title: string): Promise<IRPattern | undefined> {
  try {
    const parseDSL = await getParseDSL();
    return parseDSL(dsl, { context: 'load' });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.warn(`Failed to parse pattern "${title}": ${message}`);
    return undefined;
  }
}

/**
 * Replaces the entire state (sync pull, Settings import) and persists it.
 * Clears the session mirror when the incoming state supersedes it, so a
 * deliberate whole-state replace can't be partially undone at next boot.
 */
export async function saveFullState(state: KnittingState): Promise<void> {
  canonicalState = structuredClone(state);
  stateGeneration++;
  loadPromise = null;
  const mirror = readSessionMirror();
  if (mirror) {
    const s = canonicalState.sessions[mirror.session.id];
    // Clear when the replacement supersedes the mirror — including when the
    // mirrored session is ABSENT from it: a whole-state replace is
    // authoritative about what exists (e.g. the session was deleted on
    // another device and pulled in via sync). Leaving the mirror here would
    // let boot reconciliation resurrect the deleted session.
    if (!s || s.updatedAt >= mirror.session.updatedAt) clearSessionMirror();
  }
  return commitCanonical();
}

/**
 * Returns all patterns in the user's library.
 */
export async function getLibrary(): Promise<Record<string, Pattern>> {
  const state = await getFullState();
  return state.patterns;
}

/**
 * Saves or updates a pattern in the library.
 *
 * The mutation itself is serialized through `serializeWrite` (fast: no I/O),
 * but the durability wait (`commitCanonical()`'s promise) happens *outside*
 * that queue. Awaiting it inside `serializeWrite` would block the next
 * queued mutator behind this one's full IDB round trip, defeating write
 * coalescing — N mutators queued back-to-back would each force their own
 * write instead of batching behind a single in-flight one. This caller still
 * only resolves once its own write is durable (unchanged promise semantics).
 */
export async function savePattern(pattern: Pattern): Promise<void> {
  let pending: Promise<void> | null = null;
  await serializeWrite(async () => {
    const state = await ensureLoaded();
    state.patterns[pattern.id] = structuredClone(pattern);
    pending = commitCanonical();
  });
  if (pending) await pending;
}

/**
 * Removes a pattern from the library. With `cascadeSessions: true`, also
 * removes every session that references it. Returns the number of sessions
 * deleted (always 0 unless cascading).
 */
export async function deletePattern(
  patternId: string,
  opts: { cascadeSessions?: boolean } = {},
): Promise<{ deletedSessions: number }> {
  let pending: Promise<void> | null = null;
  const { orphanedPdfBlobId, deletedSessions } = await serializeWrite(async () => {
    const state = await ensureLoaded();
    // Capture the orphaned PDF blob id BEFORE the pattern record is dropped —
    // once it's gone the only pointer to the local-only blob is lost. Cleanup
    // happens after the write is durable; failure to delete the blob is
    // best-effort (the worst case is wasted IDB quota, not data corruption).
    const orphanedPdfBlobId = state.patterns[patternId]?.pdfRef?.id;
    delete state.patterns[patternId];
    let deletedSessions = 0;
    if (opts.cascadeSessions) {
      for (const [id, s] of Object.entries(state.sessions)) {
        if (s.patternId === patternId) {
          delete state.sessions[id];
          clearSessionMirror(id);
          deletedSessions++;
        }
      }
    }
    pending = commitCanonical();
    return { orphanedPdfBlobId, deletedSessions };
  });
  if (pending) await pending;
  if (orphanedPdfBlobId) {
    const { deletePdfBlob } = await import('./pdf');
    await deletePdfBlob(orphanedPdfBlobId).catch(() => undefined);
  }
  return { deletedSessions };
}

/**
 * Saves or updates a session. Bumps `updatedAt`; `startedAt` is set on first
 * save if missing (older Session records may not have it). Content-identical
 * saves (ignoring `updatedAt`) are skipped entirely — no `updatedAt` churn,
 * no mirror rewrite, no IDB write — *unless* this session was restored by
 * boot reconciliation and not yet confirmed by a live write, in which case
 * this write is exactly that confirmation and must proceed.
 *
 * Deliberately does NOT go through `serializeWrite`: every mutator here edits
 * the same shared `canonicalState` object synchronously (no read-then-later-
 * write gap on an independent clone), so the read/write interleaving race
 * `serializeWrite` was built for no longer exists. Routing rapid calls
 * through that queue would instead serialize them behind each other's full
 * `commitCanonical()` durability wait, defeating write coalescing — N
 * concurrent row-taps would each force their own IDB write instead of
 * batching behind one in-flight write. `pumpWrites`'s dirty/waiters
 * mechanism is what actually coalesces concurrent calls; this function only
 * needs to apply its edit and ask for a commit.
 */
export async function updateSession(session: Session): Promise<void> {
  const state = await ensureLoaded();
  const existing = state.sessions[session.id];
  const isUnconfirmedRecovery = unconfirmedRecoveredSessionId === session.id;
  if (existing && !isUnconfirmedRecovery && sessionContentEquals(existing, session)) return;
  const now = new Date().toISOString();
  const next: Session = {
    ...structuredClone(session),
    startedAt: session.startedAt || now,
    updatedAt: now,
  };
  state.sessions[session.id] = next;
  mirrorSession(next);
  confirmMirrorRecovery(session.id);
  await commitCanonical();
}

/**
 * Repoints a session at a new pattern (used after lazy fork) WITHOUT
 * disturbing any other session fields. Reads the latest session from the
 * in-memory canonical state before writing, so it never clobbers
 * history/snapshot/timer that an in-flight auto-save just applied.
 */
export async function repointSession(sessionId: string, newPatternId: string): Promise<void> {
  const state = await ensureLoaded();
  const session = state.sessions[sessionId];
  if (!session) return;
  const next: Session = {
    ...session,
    patternId: newPatternId,
    updatedAt: new Date().toISOString(),
  };
  state.sessions[sessionId] = next;
  mirrorSession(next);
  confirmMirrorRecovery(sessionId);
  await commitCanonical();
}

/**
 * Removes a session entirely.
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const state = await ensureLoaded();
  delete state.sessions[sessionId];
  clearSessionMirror(sessionId);
  confirmMirrorRecovery(sessionId);
  await commitCanonical();
}

/**
 * Compacts a session: collapses `history` into per-row timing tuples and marks
 * the session as archived. Irreversible — raw events are gone after this.
 */
export async function archiveSession(sessionId: string): Promise<void> {
  const state = await ensureLoaded();
  const session = state.sessions[sessionId];
  if (!session) return;

  const { rowTimings, totalTimeMs, currentRow } = archiveHistory(session.history);
  const activityDays = sessionActivityDays(session.history);
  const next: Session = {
    ...session,
    status: 'archived',
    history: [],
    rowTimings,
    totalTimeMs,
    activityDays,
    snapshot: { ...session.snapshot, currentRow, totalTimeMs },
    updatedAt: new Date().toISOString(),
  };
  state.sessions[sessionId] = next;
  mirrorSession(next);
  confirmMirrorRecovery(sessionId);
  await commitCanonical();
}

/**
 * Marks a session as completed (soft, reversible). History is preserved.
 */
export async function completeSession(sessionId: string): Promise<void> {
  const state = await ensureLoaded();
  const session = state.sessions[sessionId];
  if (!session) return;
  const next: Session = {
    ...session,
    status: 'completed',
    updatedAt: new Date().toISOString(),
  };
  state.sessions[sessionId] = next;
  mirrorSession(next);
  confirmMirrorRecovery(sessionId);
  await commitCanonical();
}

/**
 * Reverts a completed session back to paused. Used by the Complete screen's
 * "Undo / keep knitting" action.
 */
export async function uncompleteSession(sessionId: string): Promise<void> {
  const state = await ensureLoaded();
  const session = state.sessions[sessionId];
  if (!session) return;
  const next: Session = {
    ...session,
    status: 'paused',
    updatedAt: new Date().toISOString(),
  };
  state.sessions[sessionId] = next;
  mirrorSession(next);
  confirmMirrorRecovery(sessionId);
  await commitCanonical();
}

/**
 * Serializes the application state to a JSON string.
 */
export function exportState(state: KnittingState): string {
  return JSON.stringify(toWireState(state));
}

/**
 * Parses a JSON string into an in-memory KnittingState. Per-item validation
 * failures become opaque blobs in `unknownPatterns` / `unknownSessions` and
 * round-trip through saves; the import never throws on a single bad item.
 */
export async function importState(json: string): Promise<KnittingState> {
  const { version, patterns, sessions, unknownPatterns, unknownSessions } = await parseStateBlob(
    JSON.parse(json),
  );
  return { version, patterns, sessions, unknownPatterns, unknownSessions };
}

// Another tab wrote the blob: drop the canonical cache so the next read
// re-loads. Skipped while we have unwritten local changes — our own pending
// write will land and re-ping the other tabs; dropping dirty state here
// would lose the local mutations. Bumps `stateGeneration` the same way
// `saveFullState` / `__resetPersistenceForTest` do, so a cold load already in
// flight (from before this invalidation) discards its stale result instead of
// resurrecting the cache we just dropped.
subscribeRemoteStateChanged(() => {
  if (dirty || writeInFlight) return;
  canonicalState = null;
  loadPromise = null;
  stateGeneration++;
  notifyChange();
});
